import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { api } from "../../../api/apiClient";

import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PersonIcon from "@mui/icons-material/Person";

import PageHeader from "../../../app/layout/PageHeader";
import { PlatformUserDto } from "../../types/types";
import UpsertPlatformUserTab from "./UpsertPlatformUserTab";
import "./ManageUsersPage.css";

import "../../../css/buttons.css";
import "../../../css/cards.css";
import "../../../css/forms.css";
import "../../../css/tables.css";

interface PlatformUsersPageProps {
  currentUser?: {
    roles: string[];
    tenants: {
      tenantId: number;
      roles: string[];
    }[];
  };
  tenantId?: number;
  embedded?: boolean;
  onHeaderActionChange?: (action: ReactNode | null) => void;
}

export default function ManageUsersPage({
  currentUser,
  tenantId,
  embedded = false,
  onHeaderActionChange,
}: PlatformUsersPageProps) {
  const [users, setUsers] = useState<PlatformUserDto[]>([]);
  const [search, setSearch] = useState("");

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [confirmDisableUserId, setConfirmDisableUserId] = useState<
    string | null
  >(null);

  const [showCreateForm, setShowCreateForm] = useState(false);

  const refresh = useCallback(() => {
    api<PlatformUserDto[]>(
      tenantId
        ? `/api/tenant-administration/${tenantId}/users`
        : "/api/PlatformUser/get-all-users-include-roles",
    ).then(setUsers);
  }, [tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const scopedUsers = tenantId
      ? users.filter(
          (user) =>
            user.tenantId === tenantId ||
            user.tenants?.some((tenant) => tenant.tenantId === tenantId),
        )
      : users;
    if (!term) return scopedUsers;

    return scopedUsers.filter((u) => {
      const fullName = `${u.firstName ?? ""} ${u.lastName ?? ""}`
        .trim()
        .toLowerCase();

      return (
        u.email.toLowerCase().includes(term) ||
        fullName.includes(term) ||
        u.roles.join(" ").toLowerCase().includes(term) ||
        u.tenantRoleCodes.join(" ").toLowerCase().includes(term) ||
        (u.tenantName ?? "").toLowerCase().includes(term)
      );
    });
  }, [search, tenantId, users]);

  const userCards = useMemo(
    () =>
      filtered.map((user) => {
        const extendedUser = user as PlatformUserDto & {
          lastLogin?: string | null;
          lastLoginUtc?: string | null;
          lastLoginDate?: string | null;
        };
        const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
        const systemRoles = user.roles.length ? user.roles.join(", ") : "None";
        const tenantRoles = user.tenantRoleCodes.length
          ? user.tenantRoleCodes.join(", ")
          : "None";

        return {
          ...user,
          name: name || "-",
          roleSummary: `${systemRoles} / ${tenantRoles}`,
          status: user.enabled ? "Active" : "Disabled",
          lastLogin:
            extendedUser.lastLogin ??
            extendedUser.lastLoginUtc ??
            extendedUser.lastLoginDate ??
            null,
        };
      }),
    [filtered],
  );

  async function enableUser(id: string) {
    await api(`/api/platformuser/${id}/enabled`, {
      method: "PATCH",
      body: { enabled: true },
    });
    refresh();
  }

  async function disableUser(id: string) {
    await api(`/api/platformuser/${id}/enabled`, {
      method: "PATCH",
      body: { enabled: false },
    });

    setConfirmDisableUserId(null);
    refresh();
  }

  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingUserId(null);
  };

  const renderUserActions = (user: (typeof userCards)[number]) => (
        <div className="user-summary-actions">
          <Tooltip title="Edit user">
            <IconButton
              size="small"
              className="frontline-grid-action-button"
              onClick={() => {
                setShowCreateForm(false);
                setEditingUserId(user.id);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {!user.enabled ? (
            <Tooltip title="Enable user">
              <IconButton
                size="small"
                className="frontline-grid-action-button"
                sx={{ color: "green" }}
                onClick={() => enableUser(user.id)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : confirmDisableUserId === user.id ? (
            <>
              <Tooltip title="Confirm disable">
                <IconButton
                  size="small"
                  className="frontline-grid-action-button"
                  sx={{ color: "crimson" }}
                  onClick={() => disableUser(user.id)}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel disable">
                <IconButton
                  size="small"
                  className="frontline-grid-action-button"
                  onClick={() => setConfirmDisableUserId(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Disable user">
              <IconButton
                size="small"
                className="frontline-grid-action-button"
                sx={{ color: "crimson" }}
                onClick={() => setConfirmDisableUserId(user.id)}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {!user.hasPassword ? (
            <Tooltip title="Resend invite">
              <IconButton
                size="small"
                className="frontline-grid-action-button"
                onClick={() =>
                  api(`/api/platformuser/${user.id}/resend-invite`, {
                    method: "POST",
                    notifySuccess: "Invitation email sent.",
                  })
                }
              >
                <EmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Reset password">
              <IconButton
                size="small"
                className="frontline-grid-action-button"
                onClick={() =>
                  api(`/api/platformuser/${user.id}/password-reset`, {
                    method: "POST",
                    notifySuccess: "Password reset email sent.",
                  })
                }
              >
                <VpnKeyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
  );

  const isEditing = Boolean(showCreateForm || editingUserId);
  void currentUser;

  const addUserButton = useMemo(() => (
    <button
      type="button"
      className={`icon-button-solid ${
        isEditing
          ? "icon-button-solid--danger"
          : "icon-button-solid--success"
      }`}
      onClick={() => {
        setEditingUserId(null);
        setShowCreateForm((prev) => !prev);
      }}
      title={isEditing ? "Cancel" : "Add user"}
      aria-label={isEditing ? "Cancel" : "Add user"}
    >
      {isEditing ? (
        <CloseIcon fontSize="small" />
      ) : (
        <AddIcon fontSize="small" />
      )}
    </button>
  ), [isEditing]);

  useEffect(() => {
    if (!embedded || !onHeaderActionChange) return;

    onHeaderActionChange(addUserButton);
    return () => onHeaderActionChange(null);
  }, [addUserButton, embedded, onHeaderActionChange]);

  return (
    <div className={embedded ? "organisation-users-step" : "page-container"}>
      {!embedded && <PageHeader
        title="Users"
        subtitle="Invite users, manage access and maintain account status."
        actions={addUserButton}
      />}

      <div className="user-management-list">
        {!isEditing && (
          <div className="organisation-users-toolbar">
            <div className="search-container user-management-search">
              <div className="search-input-wrapper">
                <SearchIcon className="search-icon-left" fontSize="small" />
                <input
                  className="form-input search-input-with-icons"
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="search-clear-button"
                    onClick={() => setSearch("")}
                    aria-label="Clear user search"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="user-edit-form">
            <UpsertPlatformUserTab
              userId={editingUserId}
              initialTenantId={tenantId}
              onCreated={() => {
                cancelForm();
                refresh();
              }}
              onCancel={cancelForm}
            />
          </div>
        )}

        {!isEditing && (
          <div className="user-card-grid">
            {userCards.length === 0 && (
              <div className="card field-hint">No users found.</div>
            )}
            {userCards.map((user) => (
              <article className="user-summary-card" key={user.id}>
                <div className="user-summary-main">
                  <span className="user-summary-icon" aria-hidden="true">
                    <PersonIcon />
                  </span>
                  <div className="user-summary-copy">
                    <h3>{user.name}</h3>
                    <p>{user.email}</p>
                    <div className="user-summary-role">{user.roleSummary}</div>
                    <div className="user-summary-meta">
                      <span
                        className={`user-status-dot ${
                          user.enabled ? "" : "user-status-dot--inactive"
                        }`}
                      />
                      <span>{user.status}</span>
                      <span aria-hidden="true">|</span>
                      <span>
                        Last login {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : "-"}
                      </span>
                    </div>
                  </div>
                </div>
                <aside className="user-summary-rail" aria-label={`Actions for ${user.name}`}>
                  {renderUserActions(user)}
                </aside>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
